import { TIPO_MENU_PORTAL } from '~/modules/Helper/tipoMenuPortal';

const TIPO_MODULO_FIXO = 'Módulo Fixo';

export const SMARPANEL_BACKOFFICE_PROD_URL =
  'https://pai-prod-local.smarapd.com.br/paibackoffice';

const BACKOFFICE_POR_HOST = {
  localhost: 'http://localhost:90/paibackoffice',
  '127.0.0.1': 'http://localhost:90/paibackoffice',
  'pai-homolog.smarapd.com.br':
    'https://pai-homolog.smarapd.com.br/paibackoffice',
  'pai-test.smarapd.com.br': 'https://pai-test.smarapd.com.br/paibackoffice',
};

/**
 * Resolve a base do BackOffice pelo host atual do Portal.
 * localhost / homolog / test usam o ambiente interno; qualquer outro host
 * cai em produção (pai-prod-local).
 */
export function resolveSmarPanelBackOfficeBaseUrl(location = window.location) {
  const hostname = String(location?.hostname || '')
    .trim()
    .toLowerCase();

  return BACKOFFICE_POR_HOST[hostname] || SMARPANEL_BACKOFFICE_PROD_URL;
}

export const SMARPANEL_BACKOFFICE_BASE_URL = SMARPANEL_BACKOFFICE_PROD_URL;

export const SMARPANEL_BACKOFFICE_CHAVE_CLIENTE_PARAM = 'chaveCliente';
export const SMARPANEL_BACKOFFICE_RAZAO_SOCIAL_PARAM = 'razaoSocial';

function montarHandshakeQuery(handshake) {
  const chaveCliente = String(handshake?.chaveCliente || '').trim();
  if (!chaveCliente) return '';

  const params = new URLSearchParams();
  params.set(SMARPANEL_BACKOFFICE_CHAVE_CLIENTE_PARAM, chaveCliente);

  const razaoSocial = String(handshake?.razaoSocial || '').trim();
  if (razaoSocial) {
    params.set(SMARPANEL_BACKOFFICE_RAZAO_SOCIAL_PARAM, razaoSocial);
  }

  return `?${params.toString()}`;
}

/**
 * Monta URL absoluta com hash route do BackOffice.
 * Query string (antes do hash) identifica o cliente do Portal para a sessão única.
 */
export function buildSmarPanelBackOfficeHashUrl(hashPath, handshake) {
  const baseUrl = resolveSmarPanelBackOfficeBaseUrl().replace(/\/$/, '');
  const normalized = String(hashPath || '')
    .trim()
    .replace(/^#\/?/, '')
    .replace(/^\/+/, '');
  const handshakeQuery = montarHandshakeQuery(handshake);
  const hashRoute = normalized || 'menuportal';

  return `${baseUrl}/${handshakeQuery}#/${hashRoute}`;
}

export function openSmarPanelBackOfficeInNewTab(hashPath, handshake) {
  const url = buildSmarPanelBackOfficeHashUrl(hashPath, handshake);
  window.open(url, '_blank', 'noopener,noreferrer');
}

function normalizePortalPath(path) {
  return String(path || '')
    .trim()
    .replace(/^#?\/?/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

function flattenMenuItems(items, acc = []) {
  if (!items?.length) return acc;

  items.forEach(item => {
    acc.push(item);
    if (item.SubItens?.length) {
      flattenMenuItems(item.SubItens, acc);
    }
  });

  return acc;
}

function findBestMenuItemForPortalPath(menus, portalPath) {
  const normalized = normalizePortalPath(portalPath);
  if (!normalized || !menus?.length) return null;

  let best = null;
  let bestLength = -1;

  flattenMenuItems(menus).forEach(item => {
    const uri = normalizePortalPath(item.URI);
    if (!uri) return;

    const matches =
      normalized === uri || normalized.startsWith(`${uri}/`);

    if (matches && uri.length > bestLength) {
      best = item;
      bestLength = uri.length;
    }
  });

  return best;
}

function parseModuloVisaoFromPortalPath(portalPath) {
  const normalized = normalizePortalPath(portalPath);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] === 'fixo' && segments.length >= 3) {
    return {
      tipoModulo: TIPO_MENU_PORTAL.MODULO_FIXO,
      moduloKey: segments[1],
      visaoUri: segments[2],
    };
  }

  if (segments[0] === 'dinamico' && segments.length >= 3) {
    return {
      tipoModulo: TIPO_MENU_PORTAL.MODULO_DINAMICO,
      moduloKey: segments[1],
      visaoUri: segments[2],
    };
  }

  if (segments[0] === 'estrutura_organizacional' && segments[1] === 'detalhe' && segments[2]) {
    return {
      tipoModulo: TIPO_MENU_PORTAL.MODULO_ESPECIAL,
      moduloKey: 'estrutura_organizacional',
      estruturaId: segments[2],
    };
  }

  if (segments.length === 1) {
    return {
      tipoModulo: TIPO_MENU_PORTAL.MODULO_ESPECIAL,
      moduloKey: segments[0],
    };
  }

  return null;
}

/**
 * Espelha a regra do Menu Portal TreeView (BackOffice) para um nó com Visão/Query.
 */
export function buildSmarPanelBackOfficeHashPathFromTreeNode(treeNode) {
  if (!treeNode) return null;

  const moduloKey = treeNode?.Modulo?.Chave || '';
  const visaoId = treeNode?.Visao?.Id || null;
  const visaoUri = String(treeNode?.Visao?.Uri || '').trim();
  const queryId = treeNode?.Query?.Id || null;
  const tipo = treeNode?.Tipo || '';

  if (tipo === TIPO_MODULO_FIXO && moduloKey && visaoUri && visaoId) {
    return `${moduloKey}/visao/${visaoUri}/${visaoId}/items`;
  }

  if (moduloKey && visaoId) {
    return `${moduloKey}/visao/edit/${visaoId}`;
  }

  if (queryId) {
    return `atualizacaoportal/edit/${queryId}`;
  }

  if (moduloKey) {
    return moduloKey;
  }

  const uriPortal = normalizePortalPath(treeNode?.UriPortal || treeNode?.uriPortal);
  return uriPortal || null;
}

function buildHashPathFromMenuItem(menuItem, portalPath) {
  if (!menuItem) return null;

  const moduloKey = menuItem.ChaveModulo || '';
  const parsed = parseModuloVisaoFromPortalPath(portalPath);
  const tipo = menuItem.TipoMenuPortal;

  if (tipo === TIPO_MENU_PORTAL.MODULO_ESPECIAL) {
    if (parsed?.estruturaId) {
      return `estrutura_organizacional/edit/${parsed.estruturaId}`;
    }
    return moduloKey || normalizePortalPath(menuItem.URI);
  }

  if (
    tipo === TIPO_MENU_PORTAL.MODULO_FIXO ||
    tipo === TIPO_MENU_PORTAL.MODULO_DINAMICO
  ) {
    return moduloKey || parsed?.moduloKey || null;
  }

  if (tipo === TIPO_MENU_PORTAL.EXTERNO) {
    return 'menuportal';
  }

  return null;
}

function buildHashPathFromPortalPath(portalPath) {
  const normalized = normalizePortalPath(portalPath);

  if (!normalized) {
    return 'menuportal';
  }

  const staticRoutes = {
    smar: 'menuportal',
    sumario: 'menuportal',
    faleconosco: 'menuportal',
    estatisticas: 'menuportal',
    manutencao: 'menuportal',
    notfound: 'menuportal',
  };

  if (staticRoutes[normalized]) {
    return staticRoutes[normalized];
  }

  const parsed = parseModuloVisaoFromPortalPath(portalPath);
  if (parsed?.estruturaId) {
    return `estrutura_organizacional/edit/${parsed.estruturaId}`;
  }

  if (parsed?.moduloKey) {
    return parsed.moduloKey;
  }

  return normalized;
}

function flattenTreeViewNodes(nodes, acc = []) {
  if (!nodes?.length) return acc;

  nodes.forEach(node => {
    acc.push(node);
    const children = node.children || node.Filhos;
    if (children?.length) {
      flattenTreeViewNodes(children, acc);
    }
  });

  return acc;
}

function findTreeNodeForPortalPath(treeNodes, portalPath) {
  const normalized = normalizePortalPath(portalPath);
  if (!normalized || !treeNodes?.length) return null;

  let best = null;
  let bestLength = -1;

  flattenTreeViewNodes(treeNodes).forEach(node => {
    const uri = normalizePortalPath(node?.UriPortal || node?.uriPortal);
    if (!uri) return;

    const matches =
      normalized === uri || normalized.startsWith(`${uri}/`);

    if (matches && uri.length > bestLength) {
      best = node;
      bestLength = uri.length;
    }
  });

  return best;
}

/**
 * Resolve o hash do BackOffice para a rota atual do Portal.
 * Prioriza treeview (quando disponível) e depois o menu do Portal.
 */
export function resolveSmarPanelBackOfficeHashPath({
  portalPath,
  menus = [],
  treeViewNodes = null,
}) {
  const path = portalPath || '';

  if (treeViewNodes?.length) {
    const treeNode = findTreeNodeForPortalPath(treeViewNodes, path);
    const hashFromTree = buildSmarPanelBackOfficeHashPathFromTreeNode(treeNode);
    if (hashFromTree) {
      return hashFromTree;
    }
  }

  const menuItem = findBestMenuItemForPortalPath(menus, path);
  const hashFromMenu = buildHashPathFromMenuItem(menuItem, path);
  if (hashFromMenu) {
    return hashFromMenu;
  }

  return buildHashPathFromPortalPath(path);
}

export function resolveSmarPanelBackOfficeUrl({
  portalPath,
  menus = [],
  treeViewNodes = null,
  handshake,
}) {
  const hashPath = resolveSmarPanelBackOfficeHashPath({
    portalPath,
    menus,
    treeViewNodes,
  });

  return buildSmarPanelBackOfficeHashUrl(hashPath, handshake);
}
